/*
 * FloatingUploadIndicator.jsx — widget widoczny na WSZYSTKICH zakładkach,
 * gdy trwa wgrywanie plików. Pokazuje: liczbę aktywnych, postęp, opcję
 * zminimalizowania / rozwinięcia.
 */

import React from 'react';
import { X, ChevronDown, ChevronUp, Loader2, Check, AlertCircle } from 'lucide-react';
import { useUpload } from '../../context/UploadContext';

const FloatingUploadIndicator = () => {
  const { queue, cancel, clearFinished, stats, minimized, setMinimized } = useUpload();
  if (queue.length === 0) return null;

  const currentUploading = queue.find((it) => it.status === 'uploading');
  const anyActive = stats.active > 0;

  return (
    <div
      data-testid="floating-upload-indicator"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        width: minimized ? 260 : 360,
        maxWidth: 'calc(100vw - 48px)',
        background: 'linear-gradient(180deg, rgba(17,24,39,0.98), rgba(2,6,23,0.98))',
        border: '1px solid rgba(212, 175, 55, 0.35)',
        borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        overflow: 'hidden', color: '#e5e7eb',
      }}
    >
      {/* Header */}
      <div
        onClick={() => setMinimized(!minimized)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px', cursor: 'pointer',
          borderBottom: minimized ? 'none' : '1px solid rgba(148,163,184,0.15)',
        }}
      >
        {anyActive ? (
          <Loader2 size={18} color="#d4af37" style={{ animation: 'spin 1.5s linear infinite' }} />
        ) : stats.error > 0 ? (
          <AlertCircle size={18} color="#ef4444" />
        ) : (
          <Check size={18} color="#22c55e" />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {anyActive
              ? `Wgrywam pliki… (${stats.done}/${stats.total})`
              : stats.error > 0
                ? `Zakończono z błędami (${stats.error})`
                : `✅ Wgrano ${stats.done} pliki`}
          </div>
          {anyActive && currentUploading && (
            <div style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentUploading.file.name} · {currentUploading.progress}%
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
          data-testid="upload-widget-toggle"
          style={btnIcon}
        >{minimized ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
        {!anyActive && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clearFinished(); }}
            data-testid="upload-widget-close"
            style={btnIcon}
            title="Zamknij"
          ><X size={14} /></button>
        )}
      </div>

      {/* List */}
      {!minimized && (
        <div style={{ maxHeight: 320, overflowY: 'auto', padding: '8px 10px' }}>
          {queue.map((it) => (
            <div
              key={it.id}
              data-testid={`upload-item-${it.status}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                borderBottom: '1px solid rgba(148,163,184,0.08)', minWidth: 0,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  color: it.status === 'error' ? '#ef4444' : (it.status === 'cancelled' ? '#94a3b8' : '#e5e7eb'),
                  fontWeight: 500,
                }}>
                  {it.status === 'done' && '✓ '}
                  {it.status === 'error' && '✗ '}
                  {it.status === 'cancelled' && '⊘ '}
                  {it.file.name}
                </div>
                {it.status === 'uploading' && (
                  <div style={{ height: 3, background: 'rgba(148,163,184,0.2)', borderRadius: 2, marginTop: 4 }}>
                    <div style={{
                      height: '100%', width: `${it.progress}%`,
                      background: 'linear-gradient(90deg, #d4af37, #f6d55e)',
                      transition: 'width 0.2s ease', borderRadius: 2,
                    }} />
                  </div>
                )}
                {it.status === 'error' && (
                  <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>{it.error}</div>
                )}
              </div>
              {(it.status === 'pending' || it.status === 'uploading') && (
                <button
                  onClick={() => cancel(it.id)}
                  style={btnIcon}
                  title="Anuluj"
                ><X size={12} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const btnIcon = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 26, height: 26, borderRadius: 6,
  background: 'rgba(148,163,184,0.1)', color: '#94a3b8',
  border: '1px solid rgba(148,163,184,0.2)', cursor: 'pointer',
};

export default FloatingUploadIndicator;
